/**
 * PrLitReviewCollectionPage
 *
 * A single PR Literature Review collection, reached from under the Parasite
 * Rate topic in the sidebar. The collection's articles are topic-restricted to
 * PR by the shared context (based on the collection's type). Read-only: no
 * management action bar (those are for user collections).
 *
 * Renders a `PrLitReviewStatusChart` at the top of the page showing this
 * collection's status breakdown, followed by the standard toolbar and table.
 */

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import JournalArticlesShell, {
  redirectAwayFromForbiddenCollection,
} from "@/components/journal-articles/JournalArticlesShell"
import JournalArticlesTable, {
  buildCollectionFilterOptions,
} from "@/components/journal-articles/JournalArticlesTable"
import {
  useJournalArticlesPage,
  stripLocationColumns,
} from "@/components/journal-articles/useJournalArticlesPage"
import { useResolvedCollection } from "@/components/journal-articles/useResolvedCollection"
import PrLitReviewStatusChart, {
  CollectionStatusEntry,
} from "@/components/journal-articles/PrLitReviewStatusChart"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { Card, CardContent } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { SidebarOption } from "@/components/journal-articles/JournalArticlesSidebar"
import { PR_LITERATURE_REVIEW_TYPE } from "@/services/biblioService"
import {
  JournalArticleStatusCountsResponse,
  getJournalArticleStatusCounts,
} from "@/services/journalArticlesService"
import { useAuth } from "@/hooks/useAuth"

/** Title shown while the collection is resolving. */
const LOADING_TITLE: string = "PR Literature Review Collection"

/** Short description shown under the collection name heading. */
const PAGE_DESCRIPTION: string = "Browse journal articles in our PR Literature Review cycles"

/** The API topic slug for PR (parasite rate) articles. */
const PR_TOPIC_SLUG: string = "pr"

/**
 * The PR Literature Review collection page.
 *
 * @returns The rendered page element.
 */
export default function PrLitReviewCollectionPage(): React.JSX.Element {
  const navigate = useNavigate()
  const resolved = useResolvedCollection(PR_LITERATURE_REVIEW_TYPE)

  // On an unresolvable collection (missing / wrong type / inaccessible),
  // redirect to the all-articles view with an explanatory toast.
  useEffect(() => {
    if (resolved.status === "not-found") {
      redirectAwayFromForbiddenCollection(navigate)
    }
  }, [resolved.status, navigate])

  if (resolved.status === "ready") {
    return (
      <PrLitReviewCollectionReady
        collectionId={resolved.collection.id}
        collectionName={resolved.collection.collectionName}
      />
    )
  }

  // Loading or redirecting: keep the sidebar frame and show a spinner.
  return (
    <JournalArticlesShell title={LOADING_TITLE}>
      <Card className="flex-1 min-w-0 flex flex-col min-h-0 shadow-none">
        <CardContent className="flex flex-1 flex-col min-h-0">
          <LoadingSpinner variant="outline" loadingContent="Loading collection..." />
        </CardContent>
      </Card>
    </JournalArticlesShell>
  )
}

/**
 * Props for the ready-state inner component.
 */
interface PrLitReviewCollectionReadyProps {
  /** The resolved collection id. */
  collectionId: number
  /** The resolved collection name. */
  collectionName: string
}

/**
 * The ready-state body: a status chart at the top, followed by the standard
 * table scoped to the resolved collection. Separated so the hook calls only
 * run when the collection is resolved (avoiding conditional hook issues).
 *
 * @param props - The resolved collection props.
 * @returns The rendered table page element.
 */
function PrLitReviewCollectionReady({
  collectionId,
  collectionName,
}: PrLitReviewCollectionReadyProps): React.JSX.Element {
  const { getAccessToken } = useAuth()
  const sidebarOption: SidebarOption = `collection-${collectionId}`
  const page = useJournalArticlesPage({ sidebarOption })

  // Whether the status chart is expanded. Open by default; the user can collapse
  // it to give the article table more vertical room.
  const [chartOpen, setChartOpen] = useState<boolean>(true)

  const filterOptions = useMemo(
    () => buildCollectionFilterOptions(page.collections),
    [page.collections]
  )

  const visibleColumns = useMemo(
    () => stripLocationColumns(page.visibleColumns),
    [page.visibleColumns]
  )

  // Fetch this collection's status counts for the chart. Clear any stale counts
  // immediately when the collection id changes so the chart hides until the new
  // data arrives (rather than briefly showing the previous collection's chart).
  const [statusCounts, setStatusCounts] = useState<JournalArticleStatusCountsResponse | undefined>(
    undefined
  )
  useEffect(() => {
    setStatusCounts(undefined)
    let cancelled: boolean = false

    async function fetchCounts(): Promise<void> {
      try {
        const token: string = await getAccessToken()
        const result: JournalArticleStatusCountsResponse =
          await getJournalArticleStatusCounts(token, collectionId, PR_TOPIC_SLUG)
        if (!cancelled) {
          setStatusCounts(result)
        }
      } catch (err) {
        // The chart is a convenience, not core data, so a failure is logged
        // rather than surfaced as a page-level error. The chart simply stays
        // empty until the next load.
        console.error("Failed to fetch status counts for collection:", err)
      }
    }

    void fetchCounts()
    return () => {
      cancelled = true
    }
  }, [getAccessToken, collectionId])

  // Build the single entry the chart component expects.
  const chartEntries: CollectionStatusEntry[] = useMemo(
    () => [
      {
        collectionId,
        collectionName,
        statusCounts,
      },
    ],
    [collectionId, collectionName, statusCounts]
  )

  return (
    <JournalArticlesShell
      title={collectionName}
      description={PAGE_DESCRIPTION}
      showDetailsPanel
      searchValue={page.searchQuery}
      onSearchChange={page.setSearchQuery}
    >
      {/* Collapsible status breakdown chart for this collection. Hidden until
          the status counts have loaded, so switching collections does not show
          an empty chart for the previous/not-yet-fetched data. */}
      {statusCounts !== undefined && (
        <Collapsible open={chartOpen} onOpenChange={setChartOpen} className="mb-4">
          <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
            <ChevronDown
              className={cn(
                "size-4 transition-transform duration-200",
                chartOpen ? "rotate-0" : "-rotate-90"
              )}
            />
            Status breakdown
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <PrLitReviewStatusChart entries={chartEntries} />
          </CollapsibleContent>
        </Collapsible>
      )}

      <JournalArticlesTable page={page} filterOptions={filterOptions} visibleColumns={visibleColumns} />
    </JournalArticlesShell>
  )
}
